import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import RequestButton from './RequestButton';
import Votestars from './Votestars';

const blueHeart = '/emoji/1FA75.svg';
const emptyHeart = '/emoji/1F90D.svg';

export default function SongTable({
  songs = [],
  showTime = true,
  showFavorites = true,
  showRequest = false,
  disableRequestIf = false,
  sortable = true,
  hideRequester = false,
}) {
  const { isFavorited, toggleFavorite, authToken, votes, voteForSong, removeVoteForSong } = useAuth();

  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [userVotes, setUserVotes] = useState({});
  const [avgVotes, setAvgVotes] = useState({});

  useEffect(() => {
    const newVotes = {};
    const newAvgVotes = {};
    for (const song of songs) {
      const vote = votes[song.id];
      if (typeof vote === 'number') {
        newVotes[song.id] = vote;
      }
      newAvgVotes[song.id] = song.avg_vote;
    }
    setUserVotes(newVotes);
    setAvgVotes(newAvgVotes);
  }, [songs, votes]);

  const handleSort = (field) => {
    if (!sortable) return;
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedSongs = useMemo(() => {
    if (!sortField) return songs;

    const sorted = [...songs].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'favorited') {
        aValue = isFavorited(a.id) ? 1 : 0;
        bValue = isFavorited(b.id) ? 1 : 0;
        return aValue - bValue;
      }

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
        return aValue.localeCompare(bValue);
      }

      return aValue - bValue;
    });

    return sortDirection === 'asc' ? sorted : sorted.reverse();
  }, [songs, sortField, sortDirection, isFavorited]);

  const handleVote = async (songId, vote) => {
    const currentVote = userVotes[songId];

    try {
      if (currentVote === vote) {
        await removeVoteForSong(songId);
        setUserVotes((prev) => {
          const updated = { ...prev };
          delete updated[songId];
          return updated;
        });
        toast.success('✅ Vote removed');
      } else {
        await voteForSong(songId, vote);
        setUserVotes((prev) => ({ ...prev, [songId]: vote }));
        toast.success(`✅ Voted ${vote}`);
      }

      const res = await fetch(`/api/song/${songId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (res.ok) {
        const data = await res.json();
        setAvgVotes((prev) => ({ ...prev, [songId]: data.avg_vote || 0 }));
      }
    } catch (err) {
      toast.error('❌ Failed to update vote');
      console.error('Vote error:', err);
    }
  };

  const sortIcon = (field) => {
    if (!sortable) return null;
    if (sortField !== field) return '⇅';
    return sortDirection === 'asc' ? '⬆' : '⬇';
  };

  return (
    <table className="w-full min-w-[600px] table-auto border-collapse text-[10px]">
      <thead>
        <tr className="bg-gray-100">
          <th className="px-1 py-0 border-b w-[15%] text-left cursor-pointer" onClick={() => handleSort('artist')}>
            Artist {sortIcon('artist')}
          </th>
          <th className="px-1 py-0 border-b text-left cursor-pointer" style={{ width: 'auto' }} onClick={() => handleSort('title')}>
            Title {sortIcon('title')}
          </th>
          <th className="px-1 py-0 border-b w-[32px] text-left cursor-pointer" onClick={() => handleSort('length')}>
            Length {sortIcon('length')}
          </th>
          {showTime && (
            <th className="px-1 py-0 border-b w-[70px] text-left cursor-pointer" onClick={() => handleSort('start_time')}>
              Play Time {sortIcon('start_time')}
            </th>
          )}
          {!hideRequester && (
            <th className="px-1 py-0 border-b w-[76px] text-left cursor-pointer" onClick={() => handleSort('requested_by_username')}>
              Requested By {sortIcon('requested_by_username')}
            </th>
          )}
          <th className="px-1 py-0 border-b w-[64px] text-left cursor-pointer" onClick={() => handleSort('avg_vote')}>
            Rating {sortIcon('avg_vote')}
          </th>
          {showFavorites && (
            <th
              className="px-1 py-0 border-b w-[32px] text-left cursor-pointer"
              onClick={() => handleSort('favorited')}
            >
              Fav {sortIcon('favorited')}
            </th>
          )}
          {showRequest && <th className="px-1 py-0 border-b w-[40px] text-left">Request</th>}
        </tr>
      </thead>
      <tbody>
        {sortedSongs.map((song, idx) => {
          const isNowPlaying = song.isNowPlaying;
          const minutes = Math.floor(song.length / 60);
          const seconds = (song.length % 60).toString().padStart(2, '0');
          const playTime = song.played_at || song.start_time;
          const now = Date.now();
          const timeDiff = playTime ? Math.floor((playTime * 1000 - now) / 60000) : null;

          let playTimeStr = '-';
          if (timeDiff !== null) {
            if (song.source === 'now') playTimeStr = '▶ Now';
            else if (song.source === 'upcoming') playTimeStr = timeDiff <= 1 ? 'Next up!' : `in ${timeDiff} min${timeDiff === 1 ? '' : 's'}`;
            else if (song.source === 'history') playTimeStr = Math.abs(timeDiff) < 1 ? 'Just played' : `${Math.abs(timeDiff)} min${Math.abs(timeDiff) === 1 ? '' : 's'} ago`;
          }

          const cellClass = `px-1 py-0 ${isNowPlaying ? 'font-bold text-blue-700 bg-green-50' : ''}`;
          const displayRating = userVotes[song.id] ?? Math.round(userVotes[song.id] || 0);

          const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
          return (
            <tr
              key={`${song.id}-${song.start_time || song.played_at}`}
              className={`border-t ${rowBg}`}
            >
              <td className={cellClass + " text-[12px]"}>
                {song.artists
                  ? Object.entries(song.artists).map(([artistId, artistName], i, arr) => (
                      <span key={artistId}>
                        <Link to={`/artist/${artistId}`} className="text-blue-700 hover:underline">
                          {artistName}
                        </Link>
                        {i < arr.length - 1 && ', '}
                      </span>
                    ))
                  : <span className="text-gray-400">Unknown</span>
                }
              </td>
              <td className={cellClass + " text-[12px]"} style={{ width: 'auto' }}>
                <div className="flex items-center justify-between w-full">
                  <span>
                    <Link to={`/song/${song.id}`} className="text-blue-600 hover:underline">
                      {song.title}
                    </Link>
                  </span>
                  {song.platform_brief && (
                    <Link
                      to={`/platform/${encodeURIComponent(song.platform)}`}
                      className="ml-2 inline-block px-1 py-0 rounded bg-black text-white text-xs leading-tight font-mono"
                      style={{ lineHeight: '1.1', height: '1.1em' }}
                      title={song.platform}
                    >
                      {song.platform_brief}
                    </Link>
                  )}
                </div>
              </td>
              <td className={cellClass}>{minutes}:{seconds}</td>
              {showTime && <td className={cellClass}>{playTimeStr}</td>}
              {!hideRequester && (
                <td className={cellClass}>{song.requested_by_username || 'DJ Hypr'}</td>
              )}
              <td className={cellClass}>
                <Votestars
                  value={userVotes[song.id] ?? Math.round(userVotes[song.id] || 0)}
                  onVote={handleVote}
                  avg={avgVotes[song.id]}
                  songId={song.id}
                  disabled={!authToken} // Disable voting if not logged in
                />
              </td>
              {showFavorites && (
                <td className={cellClass}>
                  <button
                    onClick={() => toggleFavorite(song.id)}
                    className="focus:outline-none"
                    title={isFavorited(song.id) ? 'Unfavorite' : 'Favorite'}
                  >
                    <img
                      src={isFavorited(song.id) ? '/emoji/2764.svg' : '/emoji/1F90D.svg'}
                      alt={isFavorited(song.id) ? 'Favorited' : 'Not favorited'}
                      className="inline w-5 h-5 align-text-bottom"
                      draggable={false}
                    />
                  </button>
                </td>
              )}
              {showRequest && (
                <td className={cellClass}>
                  <RequestButton
                    songId={song.id}
                    recentlyPlayed={song.recently_played}
                    disabled={disableRequestIf?.(song)}
                  />
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
